import React, { useState } from 'react';
import { Container as LayoutContainer, PageHeader } from '../layout';
import { Row, Col } from 'react-bootstrap';
import styles from './InsightsArticles.module.css';

const articles = [
  {
    id: 'august-101',
    tileTitle: 'August 101: Turning Emotion into Insight',
    tileBlurb:
      'Learn what makes August different. Discover how a canvass helps you go beyond opinions to uncover emotion — transforming what people feel into insight you can act on.',
    detailTitle: 'Understanding the August Way: From Emotion to Action',
    body: [
      'At August, we believe every decision begins with emotion. Traditional surveys capture what people think. August helps you understand why they feel the way they do — and how that emotion drives behaviour.',
      'A canvass is where it starts — an adaptive, AI-guided conversation that listens deeply to each Voice. Instead of fixed questions, August adjusts in real time, following emotional cues to uncover richer insight.',
      'When enough Voices contribute, August surfaces patterns — shared motivations, barriers, and opportunities hidden beneath the surface of words. These patterns become insights, emotional data you can turn into meaningful action.',
      "With each canvass, you’re building your organization’s emotional intelligence system — a map of what drives belonging, engagement, and trust across your teams, customers, or communities.",
      'The more you listen through emotion, the clearer your decisions become. That’s the August way — purpose powered by feeling.',
    ],
  },
  {
    id: 'timing-is-emotional',
    tileTitle: 'Timing is Emotional: When to Activate Your Canvass',
    tileBlurb:
      'Find your emotional timing sweet spot. Learn how to choose the right moments to launch a canvass for richer, more authentic insights.',
    detailTitle: 'Timing is Emotional: When to Activate for the Strongest Results',
    body: [
      'When you activate your canvass matters just as much as what you ask. Emotion shifts with context — timing your canvass to match moments of curiosity, change, or reflection helps you capture more honest, energized Voices.',
      <b>Key Tips:</b>,
      <h6>After a milestone:</h6>,
      'Launch after a team win, event, or campaign when emotions are high and people want to share.',
      <h6>Before a decision:</h6>,
      'Use canvasses to test emotional readiness before launching new strategies or initiatives.',
      <h6>During change:</h6>,
      'In times of uncertainty or transition, people’s emotions guide you to what needs clarity or reassurance.',
      <h6>Not in fatigue:</h6>,
      'Avoid launching right after surveys or policy updates — people need a pause to respond authentically.',
      <h6>Consistency beats urgency:</h6>,
      'Regular, connected canvasses reveal emotional trends over time, not just reactions in the moment.',
      'The best time to activate is when you’re ready to listen, not just measure.',
    ],
  },
  {
    id: 'activation-moments',
    tileTitle: 'Finding Your Activation Moments: Where Emotion Already Lives',
    tileBlurb:
      'Your brand already has powerful moments of connection. Learn how to use existing employee, community, and customer touchpoints to capture real emotion — turning everyday engagement into actionable insight',
    detailTitle: 'Finding Your Activation Moments: Where Emotion Already Lives',
    body: [
      'The best time to listen isn’t when you create something new — it’s when emotion is already in motion. Most organizations already host powerful moments of connection: employee gatherings, community initiatives, customer experiences, sponsorships, events. These are living opportunities to understand what matters most.',
      'When you integrate August into places your brand already shows up, you meet people where they’re naturally expressive — proud, curious, inspired, sometimes frustrated.',
      'These are authentic moments of feeling, the perfect time to launch a canvass.',
      'Use canvasses to check in:',
      'Employee engagement – capture the emotion behind belonging, purpose, and recognition.',
      'Community programs – listen to what resonates with people you serve and how they feel about your impact.',
      'Customer experiences – uncover what’s driving satisfaction, loyalty, or hesitation in real time.',
      'Sponsorships and events – measure not just awareness, but how people felt in the moment.',
      'By embedding emotional listening into what’s already happening, you elevate the value of every activation — transforming moments of participation into meaningful data.',
      'Emotion is already happening around your brand. August helps you capture it — so you can amplify what matters.',
    ],
  },
  {
    id: 'real-time-relevance',
    tileTitle: 'Real-Time Relevance: Acting on Sentiment as It Happens',
    tileBlurb:
      'Emotion moves fast — your insights should too. Discover how August’s real-time analysis helps you see results as they come in, empowering teams to respond with agility, empathy, and impact.',
    detailTitle: 'Real-Time Relevance: Acting on Sentiment as It Happens',
    body: [
      'The world doesn’t wait — and neither should your data. August delivers results as Voices share them, giving you immediate visibility into emerging emotions and allowing you to act before insight becomes old news.',
      'Traditional surveys deliver static reports weeks later. By then, the moment has passed. August turns sentiment into a living feed of emotional understanding — continuously updated, always relevant.',
      'As responses arrive, you can watch emotions shift in real time: excitement building after a launch, tension rising during change, confidence growing through alignment.',
      'This live intelligence helps you course-correct quickly, celebrate immediately, or open dialogue where emotion signals need for support.',
      'Real-time relevance isn’t just about speed; it’s about responsiveness. It’s about matching your organization’s rhythm to the people who power it — employees, customers, and communities alike.',
      'When you listen in real time, you lead in real time. August turns every emotion into a timely opportunity for connection and action.',
    ],
  },
];

const InsightsArticles = () => {
  const [activeId, setActiveId] = useState(articles[0].id);
  const active = articles.find(a => a.id === activeId) || articles[0];

  return (
    <LayoutContainer fluid>
        <PageHeader
            title="Insights"
            subtext="Learn how to activate, listen, and act through emotion with August"
            align="left"
        />
        <LayoutContainer className={styles.dataContainer}>
            <Row className={styles.layout}>
                <Col  className={styles.listCol}>
                <div className={styles.list}>
                    {articles.map((a) => (
                    <button
                        key={a.id}
                        type="button"
                        className={`${styles.listItem} ${activeId === a.id ? styles.listItemActive : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); setActiveId(a.id); }}
                        onClick={() => setActiveId(a.id)}
                        aria-current={activeId === a.id ? 'true' : 'false'}
                    >
                        <div className={styles.listTitle}>{a.tileTitle}</div>
                        {activeId === a.id && (
                        <div className={styles.listBlurb}>{a.tileBlurb}</div>
                        )}
                    </button>
                    ))}
                </div>
                </Col>
                <Col md={7} className={styles.articleCol}>
                <article className={styles.article}>
                    <h2 className={styles.articleTitle}>{active.detailTitle}</h2>
                    <div className={styles.articleBody}>
                    {active.body.map((content, idx) => {
                        if (typeof content === 'string') {
                            return <p key={`p-${active.id}-${idx}`}>{content}</p>;
                        } else {
                            return <div key={`jsx-${active.id}-${idx}`}>{content}</div>;
                        }
                    })}
                    </div>
                </article>
                </Col>
            </Row>
        </LayoutContainer>

    </LayoutContainer>
  );
};

export default InsightsArticles;
